import useMoveProjectToPipelineStage, {
  UseMoveProjectToPipelineStageOptions,
} from '@/api/mutations/project/useMoveProjectToPipelineStage';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { GetPipelineStageProjectResponse } from '@/api/queries/pipelines/pipelineStageProjects';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  GetPipelineStageProjectsInput,
  GetPipelineStageQuery,
  ProjectByIdQuery,
} from '@/gql/graphql';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { original, produce } from 'immer';

type OptimisticContext = {
  currentPipelineStage: InfiniteData<GetPipelineStageQuery> | undefined;
  targetPipelineStage: InfiniteData<GetPipelineStageQuery> | undefined;
  previousProjectOverview: ProjectByIdQuery | undefined;
};

const useOptimisticMoveProjectToPipelineStage = (
  pipelinePublicId: string | undefined,
  input?: GetPipelineStageProjectsInput,
  options?: Omit<
    UseMoveProjectToPipelineStageOptions<OptimisticContext>,
    'onMutate' | 'onError' | 'onSettled'
  >
) => {
  const queryClient = useQueryClient();

  return useMoveProjectToPipelineStage({
    onMutate: async (variables) => {
      // Skip optimistic updates if pipelinePublicId is not available
      if (!pipelinePublicId) {
        return {
          currentPipelineStage: undefined,
          targetPipelineStage: undefined,
          previousProjectOverview: undefined,
        };
      }

      const { targetPipelineStagePublicId, currentPipelineStagePublicId } =
        variables;

      const currentPipelineStage = queryClient.getQueryData<
        InfiniteData<GetPipelineStageQuery>
      >(
        pipelineKeys.pipelineStageProjects(
          pipelinePublicId,
          currentPipelineStagePublicId,
          input
        )
      );
      const targetPipelineStage = queryClient.getQueryData<
        InfiniteData<GetPipelineStageQuery>
      >(
        pipelineKeys.pipelineStageProjects(
          pipelinePublicId,
          targetPipelineStagePublicId,
          input
        )
      );

      // Filter out the project from the current stage optimistically
      let filteredOutProject: GetPipelineStageProjectResponse | null = null;
      queryClient.setQueryData<InfiniteData<GetPipelineStageQuery>>(
        pipelineKeys.pipelineStageProjects(
          pipelinePublicId,
          currentPipelineStagePublicId,
          input
        ),
        produce((old) => {
          if (!old) {
            return old;
          }

          for (const page of old.pages) {
            if (!page.pipelineStage) continue;

            const index = page.pipelineStage.projects.items.findIndex(
              (project) => project.publicId === variables.projectPublicId
            );

            if (index !== -1) {
              filteredOutProject =
                original(page.pipelineStage.projects.items[index]) || null;
              page.pipelineStage.projects.items.splice(index, 1);
            }

            // Exit early if we've already filtered out the project
            if (filteredOutProject) {
              break;
            }
          }
        })
      );

      // Add the project to the target stage optimistically
      if (filteredOutProject) {
        queryClient.setQueryData<InfiniteData<GetPipelineStageQuery>>(
          pipelineKeys.pipelineStageProjects(
            pipelinePublicId,
            targetPipelineStagePublicId,
            input
          ),
          produce((old) => {
            if (!old) {
              return old;
            }

            old.pages[0].pipelineStage?.projects.items.unshift(
              filteredOutProject!
            );
          })
        );
      }

      // Update the project overview cache to reflect the new pipeline stage
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(variables.projectPublicId),
      });

      const previousProjectOverview =
        queryClient.getQueryData<ProjectByIdQuery>(
          projectKeys.overview(variables.projectPublicId)
        );

      // Get target stage info from the project's pipeline stages (already in cache)
      const targetStage =
        previousProjectOverview?.projectById.pipeline.stages?.find(
          (stage) => stage.publicId === targetPipelineStagePublicId
        );

      if (targetStage) {
        queryClient.setQueryData<ProjectByIdQuery>(
          projectKeys.overview(variables.projectPublicId),
          produce((old) => {
            if (!old) {
              return old;
            }

            old.projectById.currentPipelineStage = {
              id: targetStage.id,
              publicId: targetStage.publicId,
              title: targetStage.title,
            };
          })
        );
      }

      return {
        currentPipelineStage,
        targetPipelineStage,
        previousProjectOverview,
      };
    },
    // If the mutation fails,
    // use the result returned from onMutate to roll back
    onError: (_error, variables, context) => {
      // Skip cache rollback if pipelinePublicId is not available
      if (!pipelinePublicId) return;

      const {
        currentPipelineStage,
        targetPipelineStage,
        previousProjectOverview,
      } = context || {};

      if (currentPipelineStage) {
        queryClient.setQueryData<InfiniteData<GetPipelineStageQuery>>(
          pipelineKeys.pipelineStageProjects(
            pipelinePublicId,
            variables.currentPipelineStagePublicId,
            input
          ),
          currentPipelineStage
        );
      }

      if (targetPipelineStage) {
        queryClient.setQueryData<InfiniteData<GetPipelineStageQuery>>(
          pipelineKeys.pipelineStageProjects(
            pipelinePublicId,
            variables.targetPipelineStagePublicId,
            input
          ),
          targetPipelineStage
        );
      }

      if (previousProjectOverview) {
        queryClient.setQueryData<ProjectByIdQuery>(
          projectKeys.overview(variables.projectPublicId),
          previousProjectOverview
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      // Skip cache invalidation if pipelinePublicId is not available
      if (!pipelinePublicId) return;

      const {
        targetPipelineStagePublicId,
        currentPipelineStagePublicId,
        projectPublicId,
      } = variables;

      void queryClient.invalidateQueries({
        queryKey: pipelineKeys.pipelineStageProjects(
          pipelinePublicId,
          currentPipelineStagePublicId,
          input
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: pipelineKeys.pipelineStageProjects(
          pipelinePublicId,
          targetPipelineStagePublicId,
          input
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectPublicId),
      });
    },
    ...options,
  });
};

export default useOptimisticMoveProjectToPipelineStage;
